import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowUp, ChevronDown, GripVertical, Pencil, Trash2 } from "lucide-react";
import { observer } from "mobx-react-lite";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";

import { useQueuedMessageActions } from "../hooks/useQueuedMessageActions";
import type { QueuedChatMessage } from "../stores";

import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

interface SortableQueuedMessageProps {
  item: QueuedChatMessage;
  pauseDrainWhileEditing: boolean;
  onRemove: (id: string) => void;
  onSendImmediately: (id: string) => void;
  onUpdate: (id: string, content: string) => void;
  onEditingChange: (messageId: string, isEditing: boolean) => void;
}

const SortableQueuedMessage = ({
  item,
  pauseDrainWhileEditing,
  onRemove,
  onSendImmediately,
  onUpdate,
  onEditingChange,
}: SortableQueuedMessageProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(item.content);
  const inputRef = useRef<HTMLInputElement>(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: isEditing,
  });

  useEffect(() => {
    if (!isEditing) {
      setDraft(item.content);
    }
  }, [item.content, isEditing]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
  };

  const beginEditing = () => {
    setIsEditing(true);
    if (pauseDrainWhileEditing) {
      onEditingChange(item.id, true);
    }
  };

  const stopEditing = () => {
    setIsEditing(false);
    if (pauseDrainWhileEditing) {
      onEditingChange(item.id, false);
    }
  };

  const commitEdit = () => {
    const next = draft.trim();
    if (!next) {
      setDraft(item.content);
      stopEditing();
      return;
    }
    if (next !== item.content) {
      onUpdate(item.id, next);
    }
    stopEditing();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitEdit();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setDraft(item.content);
      stopEditing();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group flex items-center gap-2 rounded-lg px-1.5 py-1.5",
        isDragging ? "bg-gray-100" : "hover:bg-gray-50",
      )}
    >
      <button
        type="button"
        className="flex h-7 w-5 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 active:cursor-grabbing"
        aria-label="Drag to reorder queued message"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      {isEditing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
          className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 outline-none focus:border-blue-400"
        />
      ) : (
        <p className="min-w-0 flex-1 truncate text-sm text-gray-800">{item.content}</p>
      )}
      <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
        <BasicTooltip content="Edit">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-gray-500 hover:text-gray-800"
            onClick={beginEditing}
            aria-label="Edit queued message"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </BasicTooltip>
        <BasicTooltip content="Send immediately">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-gray-500 hover:text-gray-800"
            onClick={() => onSendImmediately(item.id)}
            aria-label="Send queued message immediately"
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </Button>
        </BasicTooltip>
        <BasicTooltip content="Remove">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-gray-500 hover:text-red-600"
            onClick={() => onRemove(item.id)}
            aria-label="Remove queued message"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </BasicTooltip>
      </div>
    </div>
  );
};

interface MessageQueueProps {
  className?: string;
}

const MessageQueue = observer(({ className }: MessageQueueProps) => {
  const { chatStore, sendQueuedImmediately, tryDrainQueue } = useQueuedMessageActions();
  const { queuedMessages } = chatStore;
  const [isExpanded, setIsExpanded] = useState(true);
  const [animateHeight, setAnimateHeight] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleEditingChange = useCallback(
    (messageId: string, isEditing: boolean) => {
      if (isEditing) {
        chatStore.setEditingQueuedMessageId(messageId);
        return;
      }
      if (chatStore.editingQueuedMessageId === messageId) {
        chatStore.setEditingQueuedMessageId(null);
        tryDrainQueue();
      }
    },
    [chatStore, tryDrainQueue],
  );

  if (queuedMessages.length === 0) {
    return null;
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = queuedMessages.findIndex(item => item.id === active.id);
    const newIndex = queuedMessages.findIndex(item => item.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const reordered = arrayMove(queuedMessages, oldIndex, newIndex);
    chatStore.reorderQueuedMessages(reordered.map(item => item.id));
  };

  const toggleExpanded = () => {
    setAnimateHeight(true);
    setIsExpanded(current => !current);
  };

  return (
    <div className={cn("border-b border-gray-100", className)}>
      <button
        type="button"
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
        className="flex w-full items-center gap-1.5 px-1 py-2 text-left text-sm font-medium text-gray-700 hover:text-gray-900"
      >
        <ChevronDown
          className={cn(
            "h-4 w-4 text-gray-400 transition-transform duration-100 ease-out",
            !isExpanded && "-rotate-90",
          )}
        />
        <span>{queuedMessages.length} Queued</span>
      </button>

      <div
        className={cn(
          "grid",
          animateHeight && "transition-[grid-template-rows] duration-100 ease-out",
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={queuedMessages.map(item => item.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="max-h-40 overflow-y-auto pb-2">
                {queuedMessages.map((item, index) => (
                  <SortableQueuedMessage
                    key={item.id}
                    item={item}
                    pauseDrainWhileEditing={index === 0}
                    onRemove={id => chatStore.removeQueuedMessage(id)}
                    onSendImmediately={sendQueuedImmediately}
                    onUpdate={(id, content) => chatStore.updateQueuedMessage(id, content)}
                    onEditingChange={handleEditingChange}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
});

export default MessageQueue;
